import { useMemo, useState } from 'react';
import { Icon } from '@iconify-icon/react';
import Image from 'next/image';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import { DOCS_CATALOG, DOCS_SEARCH_INDEX } from '@/components/modules/Docs/docs-catalog';
import PageLink from '@/utils/PageLink';

const normalise = (value: string) => value.toLowerCase();

const DocsPage = () => {
  const [query, setQuery] = useState('');

  const results = useMemo(() => {
    const trimmed = normalise(query.trim());
    if (!trimmed) return null;

    return DOCS_SEARCH_INDEX.filter((article) => {
      const haystack = [
        article.title,
        article.description,
        article.category,
        ...(article.keywords ?? []),
      ]
        .join(' ')
        .toLowerCase();
      // Every whitespace-separated term must appear somewhere, so "slack embed"
      // narrows rather than widening the way a plain OR match would.
      return trimmed.split(/\s+/).every((term) => haystack.includes(term));
    });
  }, [query]);

  return (
    <div className="bg-white">
      <NextSeo
        title="Documentation — AI Knowledge Agents"
        description="Guides, tutorials and the full API reference for building, deploying and operating AI knowledge agents on Chat.co."
        canonical="https://chat.co/docs"
        openGraph={{
          url: 'https://chat.co/docs',
          title: 'Documentation — AI Knowledge Agents | Chat.co',
          description:
            'Guides, tutorials and the full API reference for building, deploying and operating AI knowledge agents on Chat.co.',
          images: [
            {
              url: 'https://chat.co/images/og-docs.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co Documentation',
            },
          ],
        }}
      />

      <Header />
      <p className="mx-auto max-w-6xl px-4 py-4 text-sm text-slate-600 sm:px-6 lg:px-8">
        Evaluating Chat.co? Read the{' '}
        <Link href="/product" className="text-primary-600 underline">
          product fact sheet
        </Link>
        ,{' '}
        <Link href="/ai-chatbot-for-website" className="text-primary-600 underline">
          website chatbot guide
        </Link>
        , or{' '}
        <Link href="/internal-ai-knowledge-base" className="text-primary-600 underline">
          private knowledge base guide
        </Link>
        .
      </p>

      {/* Hero runs as two columns so it stays short vertically — the previous
          centred hero pushed the whole catalogue below the fold on its own. */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12">
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-600 ring-1 ring-slate-200">
                <Icon icon="tabler:books" className="text-sm text-primary-600" />
                Documentation
              </span>
              <h1 className="mt-4 font-display text-[2.25rem] font-semibold leading-[1.1] tracking-tight text-slate-900 sm:text-[2.75rem]">
                Build, deploy, operate
              </h1>
              <p className="mt-3 max-w-lg text-[17px] leading-relaxed text-slate-600">
                Everything it takes to run an AI knowledge agent your customers can trust — from the
                first upload to the API that drives it.
              </p>

              <div className="relative mt-5 max-w-lg">
                <Icon
                  icon="tabler:search"
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-slate-400"
                />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search the docs — try “slack”, “citations”, “api key”"
                  aria-label="Search documentation"
                  className="w-full rounded-full border border-slate-200 bg-white py-3.5 pl-12 pr-4 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-primary-400 focus:ring-4 focus:ring-primary-100"
                />
              </div>

              {/* Every section name with its article count, on one line, so the
                  shape of the documentation is legible without scrolling. */}
              {!results && (
                <nav aria-label="Jump to a section" className="mt-5 flex flex-wrap gap-2">
                  {DOCS_CATALOG.map((category) => (
                    <a
                      key={category.id}
                      href={`#${category.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[13px] font-medium text-slate-600 transition hover:border-primary-300 hover:text-primary-700">
                      <Icon icon={category.icon} className="text-sm text-primary-600" />
                      {category.title}
                      <span className="text-slate-400">{category.articles.length}</span>
                    </a>
                  ))}
                </nav>
              )}
            </div>

            <div className="hidden lg:block">
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <Image
                  src="/images/feature-upload-files.png"
                  alt="Training a Chat.co agent by pointing it at a website"
                  width={1084}
                  height={669}
                  className="h-auto w-full"
                  priority
                />
              </div>
              <p className="mt-3 text-center text-xs text-slate-500">
                {DOCS_SEARCH_INDEX.length} guides covering setup, deployment, operations and the
                API.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12">
        {results ? (
          /* Search results replace the browse view entirely — a filtered
             category grid reads as broken when most sections come back empty. */
          <div>
            <p className="mb-6 text-sm text-slate-500">
              {results.length === 0
                ? 'No articles matched.'
                : `${results.length} ${results.length === 1 ? 'article' : 'articles'} matched “${query.trim()}”`}
            </p>

            {results.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-6 py-10 text-center">
                <Icon icon="tabler:mood-search" className="text-3xl text-slate-300" />
                <p className="mt-3 text-sm text-slate-600">
                  Nothing here covers that yet. Tell us what you were looking for and we&apos;ll
                  answer directly — and usually write the page.
                </p>
                <Link
                  href={PageLink.contactUs}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-primary-700">
                  Ask support
                  <Icon icon="tabler:arrow-right" className="text-base" />
                </Link>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {results.map((article) => (
                  <Link
                    key={article.href}
                    href={article.href}
                    className="group rounded-xl border border-slate-200 bg-white p-5 transition hover:border-primary-300 hover:shadow-sm">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                      {article.category}
                    </span>
                    <h2 className="mt-1.5 text-base font-semibold text-slate-900 transition group-hover:text-primary-700">
                      {article.title}
                    </h2>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      {article.description}
                    </p>
                    <span className="mt-3 block text-xs text-slate-400">{article.readingTime}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Stacked, one full-width section per category, with room for each
             article's description. The catalogue does not fit in one screen this
             way and that is fine — the chips in the hero already carry the
             high-level map, so this view is for reading rather than orienting. */
          <div className="space-y-12">
            {DOCS_CATALOG.map((category) => (
              <section
                key={category.id}
                id={category.id}
                aria-labelledby={`docs-${category.id}`}
                className="scroll-mt-24">
                <div className="flex items-baseline gap-3 border-b border-slate-200 pb-3">
                  <Icon icon={category.icon} className="text-lg text-primary-600" />
                  <h2
                    id={`docs-${category.id}`}
                    className="font-display text-xl font-semibold text-slate-900">
                    {category.title}
                  </h2>
                  <p className="hidden text-sm text-slate-500 sm:block">{category.description}</p>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {category.articles.map((article) => (
                    <Link
                      key={article.href}
                      href={article.href}
                      className="group flex h-full flex-col rounded-xl border border-slate-200 bg-white p-5 transition hover:border-primary-300 hover:shadow-sm">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-[15px] font-semibold leading-snug text-slate-900 transition group-hover:text-primary-700">
                          {article.title}
                        </h3>
                        {article.isNew && (
                          <span className="mt-0.5 flex-shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary-700">
                            New
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 flex-1 text-sm leading-relaxed text-slate-600">
                        {article.description}
                      </p>
                      <span className="mt-4 flex items-center gap-1.5 text-xs text-slate-400">
                        <Icon icon="tabler:clock" className="text-sm" />
                        {article.readingTime}
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </section>

      {/* Support CTA */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto flex max-w-4xl flex-col items-start justify-between gap-5 px-4 py-12 sm:flex-row sm:items-center sm:px-6 lg:px-8">
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900">
              Can&apos;t find it?
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Real people read these. Tell us what you were trying to do.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              href={PageLink.contactUs}
              className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-primary-700">
              Contact support
            </Link>
            <a
              href="mailto:support@chat.co"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
              Email us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default DocsPage;
